import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, Pencil, Trash2, Upload, Download, Copy, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr } from '@/lib/format';
import { slugify } from '@/data/nav';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ImageField } from '@/components/common/ImageUploadField';

const EMPTY = {
  name: '', category: '', subcategory: '', price: '', mrp: '', stock: '',
  low_stock_threshold: 5, material: 'Metal', color: 'Gold', theme: 'Modern',
  short_description: '', description: '', sku: '', images: [], image_alt: '', image_alts: [],
  tags: [], is_new: false, is_bestseller: false, is_featured: false, is_active: true,
  meta_title: '', meta_description: '', canonical_url: '',
};
const IMG = 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=900&q=80&auto=format&fit=crop';
const BULK_ACTIONS = [
  { key: 'activate', label: 'Activate' },
  { key: 'deactivate', label: 'Deactivate' },
  { key: 'set_featured', label: 'Mark Featured' },
  { key: 'unset_featured', label: 'Remove Featured' },
  { key: 'set_new', label: 'Mark New' },
  { key: 'unset_new', label: 'Remove New' },
  { key: 'delete', label: 'Delete' },
];

export default function AdminProducts() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selection, setSelection] = useState(new Set());
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [categories, setCategories] = useState([]);
useEffect(() => {
  api.get('/admin/categories', { params: { type: 'category' } }).then((r) => setCategories(r.data || [])).catch(() => {});
}, []);

  const load = () => api.get('/admin/products', { params: { q, limit: 50 } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  const exportXlsx = async () => {
    try {
      const res = await api.get('/admin/products/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'gaze-products.xlsx'; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported products to Excel');
    } catch { toast.error('Export failed'); }
  };
  const importXlsx = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data: r } = await api.post('/admin/products/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Import complete: ${r.created} created, ${r.updated} updated${r.errors?.length ? `, ${r.errors.length} skipped` : ''}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, images: [IMG], image_alts: [''] }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY,
      ...p,
      images: (p.images && p.images.length) ? p.images : [IMG],
      image_alts: p.image_alts || (p.images || []).map(() => ''),
      tags: p.tags || [],
      low_stock_threshold: p.low_stock_threshold ?? 5,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.price) { toast.error('Name and price are required'); return; }
    const payload = {
      ...form,
      price: Number(form.price),
      mrp: Number(form.mrp || form.price),
      stock: Number(form.stock || 0),
      low_stock_threshold: Number(form.low_stock_threshold || 5),
      images: form.images?.length ? form.images.filter(Boolean) : [IMG],
      image_alts: (form.image_alts || []).map((a) => (a || '').trim()),
      rooms: form.rooms || [],
      styles: form.styles || [slugify(form.theme || 'Modern')],
      tags: (form.tags || []).map((t) => String(t).trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post('/admin/products', payload);
      toast.success(editing ? 'Product updated' : 'Product created');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => { if (!window.confirm('Delete this product?')) return; try { await api.delete(`/admin/products/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); } };
  const duplicate = async (id) => {
    try { await api.post(`/admin/products/${id}/duplicate`); toast.success('Duplicated (inactive copy created)'); load(); }
    catch { toast.error('Duplicate failed'); }
  };

  const toggleSel = (id) => setSelection((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = (checked) => setSelection(new Set(checked ? (data?.items || []).map((p) => p.id) : []));

  const runBulk = async (action) => {
    if (!selection.size) return;
    if (action === 'delete' && !window.confirm(`Delete ${selection.size} products?`)) return;
    try {
      const { data: r } = await api.post('/admin/products/bulk', { action, ids: Array.from(selection) });
      toast.success(`Bulk ${action}: ${r.affected} products`);
      setSelection(new Set()); setBulkOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk action failed'); }
  };

  const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none';
  const allChecked = (data?.items || []).length > 0 && (data?.items || []).every((p) => selection.has(p.id));
  const tagsInput = useMemo(() => (form.tags || []).join(', '), [form.tags]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3"><h1 className="text-2xl font-semibold">Products</h1>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={importXlsx} className="hidden" data-testid="admin-import-input" />
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="admin-products-import-button" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold disabled:opacity-60"><Upload size={14} /> {importing ? 'Importing...' : 'Import'}</button>
          <button onClick={exportXlsx} data-testid="admin-products-export-button" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-2 text-xs font-semibold"><Download size={14} /> Export</button>
          <button onClick={openNew} data-testid="admin-products-create-button" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-xs font-semibold text-white"><Plus size={15} /> Add Product</button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-border bg-white px-4"><Search size={16} className="text-muted-foreground" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products" className="w-full bg-transparent py-2.5 text-sm outline-none" data-testid="admin-products-search" /></div>
        {selection.size > 0 && (
          <div className="relative">
            <button onClick={() => setBulkOpen((v) => !v)} data-testid="admin-products-bulk" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--gaze-gold))]/90 px-4 py-2 text-xs font-semibold text-black">
              {selection.size} selected <ChevronDown size={13} />
            </button>
            {bulkOpen && (
              <div className="absolute right-0 z-10 mt-1 w-52 overflow-hidden rounded-xl border border-border bg-white shadow-lg">
                {BULK_ACTIONS.map((a) => (
                  <button key={a.key} onClick={() => runBulk(a.key)} className={`block w-full px-3 py-2 text-left text-xs hover:bg-[hsl(var(--gaze-ivory))] ${a.key === 'delete' ? 'text-destructive' : ''}`} data-testid={`admin-bulk-${a.key}`}>{a.label}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-3"><input type="checkbox" checked={allChecked} onChange={(e) => selectAll(e.target.checked)} data-testid="admin-select-all" /></th>
              <th className="p-3">Product</th><th className="p-3">SKU</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3">Active</th><th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="p-3"><input type="checkbox" checked={selection.has(p.id)} onChange={() => toggleSel(p.id)} data-testid="admin-select-row" /></td>
                <td className="p-3"><div className="flex items-center gap-3"><img src={p.images?.[0]} alt={p.image_alt || p.name} className="h-10 w-10 rounded-lg object-cover" /><div><p className="font-medium">{p.name}</p><p className="text-[10px] text-muted-foreground">{(p.tags || []).slice(0, 3).map((t) => `#${t}`).join(' ')}</p></div></div></td>
                <td className="p-3 text-xs text-muted-foreground">{p.sku}</td>
                <td className="p-3 text-xs text-muted-foreground">{p.category_name}{p.subcategory ? ` › ${p.subcategory}` : ''}</td>
                <td className="p-3 font-semibold">{inr(p.price)}</td>
                <td className="p-3"><span className={p.stock === 0 ? 'text-destructive' : p.stock <= (p.low_stock_threshold ?? 5) ? 'text-amber-600' : ''}>{p.stock}</span></td>
                <td className="p-3">{p.is_active ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">Active</span> : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700">Inactive</span>}</td>
                <td className="p-3"><div className="flex gap-1">
                  <button onClick={() => openEdit(p)} title="Edit" className="grid h-8 w-8 place-items-center rounded-lg border border-border" data-testid="admin-product-edit"><Pencil size={13} /></button>
                  <button onClick={() => duplicate(p.id)} title="Duplicate" className="grid h-8 w-8 place-items-center rounded-lg border border-border" data-testid="admin-product-duplicate"><Copy size={13} /></button>
                  <button onClick={() => del(p.id)} title="Delete" className="grid h-8 w-8 place-items-center rounded-lg border border-border text-destructive" data-testid="admin-product-delete"><Trash2 size={13} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No products found.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto"><DialogHeader><DialogTitle>{editing ? 'Edit Product' : 'New Product'}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className="text-xs font-medium">Name</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="admin-product-name" /></div>
            <div><label className="text-xs font-medium">Category</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={field}>
  <option value="">Select category</option>
  {categories.filter((c) => c.active !== false).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
</select></div>
            <div><label className="text-xs font-medium">Subcategory</label><input value={form.subcategory || ''} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className={field} placeholder="Optional" data-testid="admin-product-subcategory" /></div>
            <div><label className="text-xs font-medium">SKU</label><input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className={field} /></div>
            <div><label className="text-xs font-medium">Price (₹)</label><input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} data-testid="admin-product-price" /></div>
            <div><label className="text-xs font-medium">MRP (₹)</label><input type="number" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className={field} /></div>
            <div><label className="text-xs font-medium">Stock</label><input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={field} data-testid="admin-product-stock" /></div>
            <div><label className="text-xs font-medium">Low-stock threshold</label><input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className={field} data-testid="admin-product-lowstock" /></div>
            <div><label className="text-xs font-medium">Material</label><input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className={field} /></div>
            <div><label className="text-xs font-medium">Color</label><input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className={field} /></div>
            <div><label className="text-xs font-medium">Style / Theme</label><input value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className={field} /></div>
            <div className="sm:col-span-2"><label className="text-xs font-medium">Tags (comma-separated)</label><input value={tagsInput} onChange={(e) => setForm({ ...form, tags: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} className={field} placeholder="handmade, brass, gifting" data-testid="admin-product-tags" /></div>
            {(form.images || []).map((url, i) => (
              <div key={i} className="sm:col-span-2 grid gap-2 sm:grid-cols-2">
                <ImageField
                  label={<span>Image {i + 1} {i === 0 && <span className="text-muted-foreground">(main)</span>}</span>}
                  value={url}
                  onChange={(v) => {
                    const arr = [...form.images]; arr[i] = v;
                    setForm({ ...form, images: arr });
                  }}
                  ratio="aspect-square"
                  previewClass="max-w-[120px]"
                  testid={`admin-product-image-${i}`}
                />
                <div>
                  <label className="text-xs font-medium">Alt text</label>
                  <input value={form.image_alts?.[i] || ''} onChange={(e) => {
                    const arr = [...(form.image_alts || [])];
                    while (arr.length <= i) arr.push('');
                    arr[i] = e.target.value;
                    setForm({ ...form, image_alts: arr });
                  }} className={field} placeholder="Describe this image" data-testid={`admin-product-alt-${i}`} />
                </div>
              </div>
            ))}
            <div className="sm:col-span-2 flex gap-2">
              <button type="button" onClick={() => setForm({ ...form, images: [...(form.images || []), ''], image_alts: [...(form.image_alts || []), ''] })} className="rounded-full border border-border bg-white px-3 py-1 text-xs">+ Add image</button>
              {form.images?.length > 1 && <button type="button" onClick={() => setForm({ ...form, images: form.images.slice(0, -1), image_alts: (form.image_alts || []).slice(0, -1) })} className="rounded-full border border-border bg-white px-3 py-1 text-xs">Remove last</button>}
            </div>
            <div className="sm:col-span-2"><label className="text-xs font-medium">Short description</label><input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className={field} /></div>
            <div className="sm:col-span-2"><label className="text-xs font-medium">Description</label><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} /></div>

            <div className="sm:col-span-2 mt-2 rounded-xl border border-border bg-[hsl(var(--gaze-ivory))] p-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">SEO</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <div className="sm:col-span-2"><label className="text-xs font-medium">Meta title</label><input value={form.meta_title || ''} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} className={field} data-testid="admin-product-metatitle" placeholder="Shown in search & browser tab" /></div>
                <div className="sm:col-span-2"><label className="text-xs font-medium">Meta description</label><textarea rows={2} value={form.meta_description || ''} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className={field} data-testid="admin-product-metadesc" placeholder="~150 characters. Shown in search results." /></div>
                <div className="sm:col-span-2"><label className="text-xs font-medium">Canonical URL</label><input value={form.canonical_url || ''} onChange={(e) => setForm({ ...form, canonical_url: e.target.value })} className={field} data-testid="admin-product-canonical" placeholder="https://gazedecor.in/product/..." /></div>
              </div>
            </div>

            <div className="sm:col-span-2 flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} /> New</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_bestseller} onChange={(e) => setForm({ ...form, is_bestseller: e.target.checked })} /> Best Seller</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured</label>
            </div>
          </div>
          <button onClick={save} data-testid="admin-product-save" className="mt-4 w-full rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white">{editing ? 'Update' : 'Create'} Product</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
